import { errorHandler } from 'App/utils';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { Button, Col, Row } from 'react-bootstrap';
import React from 'react';
import styled from '@emotion/styled';
import GenderSelect from 'App/components/FindPatient/GenderSelect';
import { FaInfoCircle } from 'react-icons/fa';
import { TextField } from 'Shared/form/TextField';
import ToggleSwitchField from 'Shared/form/ToggleSwitchField';
import UpdateSearchParams from 'App/components/FindPatient/UpdateSearchParams';
import { DatePickerField } from 'Shared/form/DatePickerField';
import * as Yup from 'yup';
import useFeatureFlag from 'App/hooks/useFeatureFlag';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';
import FindPatientPdxTable from './FindPatientPdxTable';
import { getPDXPatient, searchPDX, searchSTM } from './FindPatientPdx.utils';
import SubmitButton from 'Shared/form/SubmitButton';

const propTypes = {
    paramFirstName: PropTypes.string,
    paramLastName: PropTypes.string,
};

const defaultProps = {
    paramFirstName: '',
    paramLastName: '',
};
const PaddedRow = styled(Row)`
    padding: 0.5% 0;
`;

const initialValues = {
    searchParams: {
        firstName: '',
        lastName: '',
        birthDate: null,
        gender: null,
        city: '',
        state: '',
        zip: '',
        externalId: '',
        showDisabled: false,
    },
    data: {
        results: [],
    },
};
const message = 'You need to fill at least 2 fields to perform the search.';

const schema = Yup.object().shape({
    searchParams: Yup.object()
        .shape({
            firstName: Yup.string()
                .trim()
                .min(2, 'First Name must be at least 2 characters')
                .max(255, 'First Name cannot exceed 255 characters')
                .nullable()
                .optional(),
            lastName: Yup.string()
                .trim()
                .min(2, 'Last Name must be at least 2 characters')
                .max(255, 'Last Name cannot exceed 255 characters')
                .nullable()
                .optional(),
            birthDate: Yup.date().nullable().typeError('Birth Date must be a valid date'),
            gender: Yup.string().nullable().optional(),
            city: Yup.string().trim().max(100, 'City cannot exceed 100 characters').nullable().optional(),
            state: Yup.string()
                .trim()
                .min(2, 'State must be 2 characters')
                .max(2, 'State must be 2 characters')
                .nullable()
                .optional(),
            zip: Yup.string()
                .trim()
                .matches(/^[0-9]*$/, 'The zip is not in the correct format')
                .min(5, 'Zip must be at least 5 digits')
                .max(15, 'Zip cannot exceed 15 digits')
                .nullable()
                .optional(),
            externalId: Yup.string()
                .trim()
                .matches(/^[0-9]*$/, 'The rx.com id is not in the correct format')
                .max(22, 'Rx.com ID cannot exceed 22 digits')
                .nullable()
                .optional(),
            showDisabled: Yup.boolean(),
            pdxFirst: Yup.boolean(),
        })
        .test('at-least-two-fields', message, (values) => {
            if (!values) return false;
            if (values.externalId) return true;
            const fieldsToCheck = ['firstName', 'lastName', 'birthDate', 'gender', 'city', 'state', 'zip'];
            const filled = fieldsToCheck.filter((key) => {
                const val = values[key];
                return val !== null && val !== undefined && String(val).trim() !== '';
            });
            return filled.length >= 2;
        }),
});

const FindPatientPdxForm = (props) => {
    const { paramFirstName, paramLastName } = props;
    const isSlsEnabled = useFeatureFlag(PERMISSION_TYPES.ENABLE_PDX_SLS);

    return (
        <div data-testid="findPdxPatientForm">
            <Formik
                validationSchema={schema}
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting, setFieldValue }) => {
                    const { searchParams } = values;
                    const { externalId, pdxFirst, showDisabled } = searchParams;
                    let results = [];
                    setSubmitting(true);
                    try {
                        if (externalId) {
                            // remove all other fields
                            setFieldValue('searchParams', {
                                ...initialValues.searchParams,
                                externalId: externalId.trim(),
                                pdxFirst,
                                showDisabled,
                            });
                            // call getPDXPatient method
                            results = await getPDXPatient(externalId.trim(), isSlsEnabled);
                        } else if (pdxFirst) {
                            // call searchPDX method
                            results = await searchPDX(searchParams, isSlsEnabled);
                        } else {
                            results = await searchSTM(searchParams).then((data) => {
                                if (data.length) {
                                    return data;
                                }
                                return searchPDX(searchParams, isSlsEnabled);
                            });
                        }
                        setFieldValue('data', {
                            results,
                            total: results.length,
                        });
                    } catch (error) {
                        errorHandler(error);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting, handleSubmit, values, setFieldValue, submitCount }) => {
                    return (
                        <>
                            <UpdateSearchParams
                                paramFirstName={paramFirstName}
                                paramLastName={paramLastName}
                            />
                            <form onSubmit={handleSubmit} className="find-patient-form">
                                <PaddedRow>
                                    <Col md={3}>
                                        <TextField
                                            fieldName="searchParams.lastName"
                                            label="Last Name"
                                            placeholder="Last Name"
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <TextField
                                            fieldName="searchParams.firstName"
                                            label="First Name"
                                            placeholder="First Name"
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <DatePickerField fieldName="searchParams.birthDate" label="Birth Date" />
                                    </Col>
                                    <Col md={3}>
                                        <GenderSelect label="Sex" />
                                    </Col>
                                </PaddedRow>
                                <PaddedRow>
                                    <Col md={3}>
                                        <TextField fieldName="searchParams.city" label="City" placeholder="City" />
                                    </Col>
                                    <Col md={3}>
                                        <TextField fieldName="searchParams.state" label="State" placeholder="State" />
                                    </Col>
                                    <Col md={3}>
                                        <TextField fieldName="searchParams.zip" label="Zip" placeholder="Zip" />
                                    </Col>
                                    <Col md={3}>
                                        <TextField
                                            fieldName="searchParams.externalId"
                                            label="Rx.com ID"
                                            placeholder="Rx.com ID"
                                        />
                                    </Col>
                                </PaddedRow>

                                <PaddedRow>
                                    <Col md={6}>
                                        <ToggleSwitchField
                                            fieldName="searchParams.showDisabled"
                                            label="Show Disabled Records"
                                        />
                                        <ToggleSwitchField
                                            fieldName="searchParams.pdxFirst"
                                            label="Search only Rx.com"
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <div className="form-button-group">
                                            <Button
                                                variant="outline-secondary"
                                                type="button"
                                                disabled={isSubmitting}
                                                onClick={() =>
                                                    setFieldValue('searchParams', initialValues.searchParams)
                                                }
                                            >
                                                Reset
                                            </Button>
                                            <SubmitButton disabled={isSubmitting}>Search</SubmitButton>
                                        </div>
                                        <div className="form-button-group">
                                            <FaInfoCircle />
                                            <span>{message}</span>
                                        </div>
                                    </Col>
                                </PaddedRow>

                                <PaddedRow>
                                    <Col md={12}>
                                        <h3 className="section-title">Search Results</h3>
                                    </Col>
                                    {submitCount < 1 && (
                                        <Col md={12}>
                                            <FaInfoCircle style={{ marginRight: 5 }} />
                                            <span>
                                                Search results based on the search criteria above will display here.
                                            </span>
                                        </Col>
                                    )}
                                </PaddedRow>
                            </form>

                            {submitCount > 0 ? <FindPatientPdxTable data={values.data?.results ?? []} /> : null}
                        </>
                    );
                }}
            </Formik>
        </div>
    );
};

FindPatientPdxForm.propTypes = propTypes;
FindPatientPdxForm.defaultProps = defaultProps;

export default FindPatientPdxForm;
